import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import {
  BottomSheet as BottomSheetComponent,
  type BottomSheetRef,
} from "react-spring-bottom-sheet";
import "./style.css";

interface BottomSheetProps {
  description?: string;
  homepage?: string;
  meta?: any;
  options: {
    openTo: "top" | "middle" | "bottom";
    positions: {
      bottom?: number;
      middle?: number;
      top?: number;
    };
    expandOnContentDrag: boolean;
    showHandle: boolean;
    open?: boolean;
  };
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (state: boolean) => void;
  withSpecialHeader?: boolean;
  shouldSnapToTop?: boolean;
  onPositionChange?: (direction: "draggingUp" | "draggingDown") => void;
}

const BottomSheet: FC<BottomSheetProps> = ({
  options,
  header,
  footer,
  children,
  open,
  setOpen,
  withSpecialHeader = false,
  shouldSnapToTop = false,
  onPositionChange,
}) => {
  const [isAtTop, setIsAtTop] = useState(true);
  const [currentPosition, setCurrentPosition] = useState<
    "bottom" | "middle" | "top"
  >(options.openTo);
  const defaultPositions = {
    bottom: 100,
    middle: 400,
    top: window.innerHeight - 100,
  };

  const bottomPosition = options.positions.bottom || defaultPositions.bottom;
  const middlePosition = options.positions.middle || defaultPositions.middle;
  const topPosition = options.positions.top || defaultPositions.top;
  const sheetRef = useRef<BottomSheetRef>(null);
  const position = options.open || options.openTo;

  const [scrollPosition, setScrollPosition] = useState(0);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [hasBeenAtTopLongEnough, setHasBeenAtTopLongEnough] = useState(false);
  const scrollAtTopTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dragTrackingRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const lastDirectionRef = useRef<"draggingUp" | "draggingDown" | null>(null);
  const verificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (sheetRef.current) {
      switch (position) {
        case "top":
          sheetRef.current.snapTo(topPosition, { source: "snap-to-top" });
          break;
        case "middle":
          sheetRef.current.snapTo(middlePosition, { source: "snap-to-middle" });
          break;
        default:
          sheetRef.current.snapTo(bottomPosition, { source: "snap-to-bottom" });
          break;
      }
    }
  }, [position, options.open]);

  const onDismiss = () => {
    if (setOpen) {
      setOpen(false);
    }
  };

  const onSpringEnd = (event: any) => {
    const height = sheetRef.current?.height || 0;

    // Stop drag tracking
    isDraggingRef.current = false;
    dragTrackingRef.current = null;
    lastDirectionRef.current = null;

    // Clear verification timer
    if (verificationTimerRef.current) {
      clearTimeout(verificationTimerRef.current);
      verificationTimerRef.current = null;
    }

    if (height <= bottomPosition + 50) {
      setCurrentPosition("bottom");
      setIsAtTop(false);
    } else if (height <= middlePosition + 50) {
      setCurrentPosition("middle");
      setIsAtTop(false);
    } else {
      setCurrentPosition("top");
      setIsAtTop(true);
    }
  };

  useEffect(() => {
    const sheet = sheetRef.current;
    if (sheet) {
      const ideaMapList = document.getElementById("ideaMapList");
      const container =
        ideaMapList || document.querySelector("[data-rsbs-scroll]");

      if (container instanceof HTMLElement) {
        const handleScroll = () => {
          const newScrollPosition = container.scrollTop;
          setScrollPosition(newScrollPosition);
          const isAtTopNow = newScrollPosition === 0;
          setIsAtTop(isAtTopNow);

          // Clear any existing timer
          if (scrollAtTopTimerRef.current) {
            clearTimeout(scrollAtTopTimerRef.current);
            scrollAtTopTimerRef.current = null;
          }

          // If we're at the top, start a timer
          if (isAtTopNow) {
            setHasBeenAtTopLongEnough(false); // Reset state while waiting
            scrollAtTopTimerRef.current = setTimeout(() => {
              setHasBeenAtTopLongEnough(true);
            }, 150);
          } else {
            setHasBeenAtTopLongEnough(false);
          }
        };

        // Run initial check
        handleScroll();

        container.addEventListener("scroll", handleScroll);
        return () => {
          if (scrollAtTopTimerRef.current) {
            clearTimeout(scrollAtTopTimerRef.current);
            scrollAtTopTimerRef.current = null;
          }
          container.removeEventListener("scroll", handleScroll);
        };
      }
    }
  }, [currentPosition]); // Add currentPosition as dependency to re-run when position changes

  const handleTouchStart = (event: Event) => {
    const touchEvent = event as TouchEvent;
    const touch = touchEvent.touches[0];
    setTouchStartY(touch.clientY);
  };

  const touchMoveHandler = (event: Event) => {
    const touchEvent = event as TouchEvent;
    if (
      touchStartY !== null &&
      currentPosition !== "bottom" &&
      hasBeenAtTopLongEnough &&
      scrollPosition === 0
    ) {
      const touch = touchEvent.touches[0];
      const deltaY = touch.clientY - touchStartY;

      if (deltaY > 100 && sheetRef.current) {
        sheetRef.current.snapTo(bottomPosition, { source: "snap-to-bottom" });
        setTouchStartY(null);
        setHasBeenAtTopLongEnough(false);
      }
    }
  };

  const handleTouchEnd = () => {
    setTouchStartY(null);
  };

  useEffect(() => {
    const element = document.querySelector("[data-rsbs-scroll]");
    if (element) {
      element.addEventListener("touchstart", handleTouchStart as EventListener);
      element.addEventListener("touchmove", touchMoveHandler as EventListener);
      element.addEventListener("touchend", handleTouchEnd);

      return () => {
        element.removeEventListener(
          "touchstart",
          handleTouchStart as EventListener,
        );
        element.removeEventListener(
          "touchmove",
          touchMoveHandler as EventListener,
        );
        element.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [
    scrollPosition,
    isAtTop,
    bottomPosition,
    touchStartY,
    hasBeenAtTopLongEnough,
    currentPosition,
  ]);

  useEffect(() => {
    if (!shouldSnapToTop || !sheetRef.current) return;
    sheetRef.current?.snapTo(topPosition, { source: "snap-to-top" });
  }, [sheetRef.current, shouldSnapToTop]);

  return (
    <BottomSheetComponent
      ref={sheetRef}
      open={Boolean(options.open || open)}
      onDismiss={setOpen ? onDismiss : undefined}
      onSpringEnd={onSpringEnd}
      onSpringStart={(e) => {
        console.log("spring start", e);
        if (
          e?.source !== "dragging" &&
          e?.source !== "snap-to-top" &&
          e?.source !== "snap-to-middle" &&
          e?.source !== "snap-to-bottom"
        )
          return;

        isDraggingRef.current = true;
        dragTrackingRef.current = sheetRef.current?.height || 0;
        const threshold = 150; // Minimum distance to consider as a direction change

        const trackDrag = () => {
          if (!isDraggingRef.current || !sheetRef.current) return;

          const currentHeight = sheetRef.current.height;
          const previousHeight = dragTrackingRef.current;

          if (previousHeight !== null) {
            const heightDifference = currentHeight - previousHeight;

            if (Math.abs(heightDifference) > threshold) {
              const newDirection =
                heightDifference > 0 ? "draggingUp" : "draggingDown";

              // Only fire if direction changed or it's been a while
              if (lastDirectionRef.current !== newDirection) {
                if (heightDifference > 0) {
                  console.log(
                    "Dragging UP - height increased by:",
                    heightDifference,
                  );
                  onPositionChange?.("draggingUp");
                } else {
                  console.log(
                    "Dragging DOWN - height decreased by:",
                    Math.abs(heightDifference),
                  );
                  onPositionChange?.("draggingDown");
                }

                lastDirectionRef.current = newDirection;

                // Set up verification timer
                if (verificationTimerRef.current) {
                  clearTimeout(verificationTimerRef.current);
                }
                verificationTimerRef.current = setTimeout(() => {
                  // Double-check if we're still dragging in the same direction
                  if (isDraggingRef.current && sheetRef.current) {
                    const verificationHeight = sheetRef.current.height;
                    const verificationDifference =
                      verificationHeight - currentHeight;

                    if (Math.abs(verificationDifference) > threshold) {
                      const verificationDirection =
                        verificationDifference > 0
                          ? "draggingUp"
                          : "draggingDown";

                      if (verificationDirection === newDirection) {
                        console.log(
                          "Verification: Still dragging",
                          newDirection,
                          "by",
                          Math.abs(verificationDifference),
                        );
                        onPositionChange?.(verificationDirection);
                      } else {
                        console.log(
                          "Verification: Direction changed to",
                          verificationDirection,
                          "by",
                          Math.abs(verificationDifference),
                        );
                        onPositionChange?.(verificationDirection);
                        lastDirectionRef.current = verificationDirection;
                      }
                    }
                  }
                }, 100); // Check after 100ms
              }
            }
          }

          dragTrackingRef.current = currentHeight;
          requestAnimationFrame(trackDrag);
        };

        requestAnimationFrame(trackDrag);
      }}
      blocking={false}
      defaultSnap={() => {
        switch (position) {
          case "top":
            return topPosition;
          case "middle":
            return middlePosition;
          default:
            return bottomPosition;
        }
      }}
      snapPoints={() => [bottomPosition, middlePosition, topPosition]}
      expandOnContentDrag={false}
      style={{
        // @ts-expect-error accept css vars as style props
        "--rsbs-overlay-rounded": options.radius
          ? `${options.radius}px`
          : position === "top"
            ? "0px"
            : "20px",
        // "--rsbs-backdrop-bg": "white",
        "--rsbs-handle-bg":
          options.showHandle === false
            ? "transparent"
            : "hsla(0, 0%, 0%, 0.14)",
        "--rsbs-header-draggable":
          options.showHandle === false ? "none" : "all",
        // --rsbs-max-w: auto;
        // --rsbs-ml: env(safe-area-inset-left);
        // --rsbs-mr: env(safe-area-inset-right);
        // "--rsbs-bg": "grey",
        // "--rsbs-max-w": useBreakpointValue({ base: "24rem", lg: "48rem" })
        zIndex: 0,
        // Override the default z-index values
        "--rsbs-header-z-index": "1",
        "--rsbs-footer-z-index": "10",
      }}
      header={
        withSpecialHeader ? (
          <div className="">
            {header}

            <div
              className={`h-48 absolute top-0 left-0 right-0  z-10 transition-opacity duration-200 ${currentPosition === "bottom" ? "visible" : "hidden"}`}
            />
          </div>
        ) : (
          header
        )
        // <div
        //   onClick={() => {
        //     setTimeout(() => {
        //       options?.setPosition("top");
        //       options?.setOpen("top");
        //     }, 0);
        //   }}
        // >
        //   {header}
        // </div>
      }
      footer={footer}
      scrollLocking={false}
    >
      {children}
    </BottomSheetComponent>
  );
};

export default BottomSheet;
